/**
 * Utilitários para gerenciamento de autenticação e sessão
 */

export interface SessionExpiredOptions {
  message?: string
  redirectToLogin?: boolean
  showNotification?: boolean
  redirectDelay?: number
}

// Flag global para evitar múltiplas notificações simultâneas
let isShowingNotification = false

/**
 * Limpa dados de autenticação do sessionStorage e localStorage
 */
export const clearAuthData = (): void => {
  sessionStorage.removeItem('access_token')
  sessionStorage.removeItem('refresh_token')
  sessionStorage.removeItem('user')
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')
}

/**
 * Alias para clearAuthData - limpa tokens inválidos automaticamente
 * Usado quando detectamos que os tokens estão corrompidos ou inválidos
 */
export const clearInvalidTokens = clearAuthData

/**
 * Verifica se há tokens válidos no sessionStorage
 */
export const hasAuthTokens = (): boolean => {
  const accessToken = sessionStorage.getItem('access_token')
  const refreshToken = sessionStorage.getItem('refresh_token')
  return !!(accessToken && refreshToken)
}

/**
 * Trata sessão expirada de forma amigável
 * Mostra mensagem e redireciona para login
 */
export const handleSessionExpired = (options: SessionExpiredOptions = {}): void => {
  // Evitar múltiplas notificações simultâneas
  if (isShowingNotification) {
    return
  }

  const {
    message = 'Sua sessão expirou. Redirecionando para login...',
    redirectToLogin = true,
    showNotification = true,
    redirectDelay = 3000,
  } = options

  isShowingNotification = true

  // Persistir mensagem para exibir após reload (ex: na tela de login)
  try {
    sessionStorage.setItem('session_expired_message', message)
    sessionStorage.setItem('session_expired_timestamp', Date.now().toString())
  } catch {
    // Ignorar erros de storage
  }

  // Limpar dados de autenticação
  clearAuthData()

  // Mostrar mensagem amigável
  if (showNotification && typeof document !== 'undefined') {
    // Logger não disponível neste contexto (utils básico)
    // console.warn('🔒 Sessão expirada:', message)  // Comentado - não crítico, mensagem já exibida ao usuário
    
    // Criar elemento de notificação amigável
    const notification = document.createElement('div')
    notification.id = 'session-expired-notification'
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
      color: white;
      padding: 20px 32px;
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 15px;
      max-width: 500px;
      min-width: 350px;
      animation: slideDown 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      text-align: center;
    `
    
    const messageText = document.createElement('div')
    messageText.style.cssText = `
      font-weight: 600;
      margin-bottom: 8px;
      font-size: 16px;
    `
    messageText.textContent = '🔒 Sessão Expirada'
    
    const detailText = document.createElement('div')
    detailText.style.cssText = `
      font-size: 14px;
      opacity: 0.95;
      margin-bottom: 12px;
    `
    detailText.textContent = message
    
    const countdownText = document.createElement('div')
    countdownText.style.cssText = `
      font-size: 12px;
      opacity: 0.85;
      font-weight: 500;
    `
    let countdown = Math.floor(redirectDelay / 1000)
    countdownText.textContent = `Redirecionando em ${countdown} segundo${countdown !== 1 ? 's' : ''}...`
    
    notification.appendChild(messageText)
    notification.appendChild(detailText)
    notification.appendChild(countdownText)
    document.body.appendChild(notification)

    // Atualizar countdown
    const countdownInterval = setInterval(() => {
      countdown--
      if (countdown <= 0) {
        clearInterval(countdownInterval)
        countdownText.textContent = 'Redirecionando agora...'
      } else {
        countdownText.textContent = `Redirecionando em ${countdown} segundo${countdown !== 1 ? 's' : ''}...`
      }
    }, 1000)

    // Remover após o delay
    setTimeout(() => {
      notification.style.animation = 'slideUp 0.3s ease-out'
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification)
        }
        isShowingNotification = false
      }, 300)
    }, redirectDelay)
  }

  // Redirecionar para login após o delay (evitar loop se já está no /login)
  const isOnLogin = typeof window !== 'undefined' && window.location.pathname === '/login'
  if (redirectToLogin && !isOnLogin) {
    setTimeout(() => {
      window.location.href = '/login'
    }, redirectDelay)
  }
}

/**
 * Adiciona estilos CSS para animação de notificação (se não existirem)
 */
const addNotificationStyles = (): void => {
  if (document.getElementById('auth-notification-styles')) {
    return
  }

  const style = document.createElement('style')
  style.id = 'auth-notification-styles'
  style.textContent = `
    @keyframes slideDown {
      from {
        transform: translateX(-50%) translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateX(-50%) translateY(0);
        opacity: 1;
      }
    }
    @keyframes slideUp {
      from {
        transform: translateX(-50%) translateY(0);
        opacity: 1;
      }
      to {
        transform: translateX(-50%) translateY(-100%);
        opacity: 0;
      }
    }
  `
  document.head.appendChild(style)
}

// Adicionar estilos quando o módulo for carregado
if (typeof document !== 'undefined') {
  addNotificationStyles()
}

